<template>
	<div id="LoginSelectionComponent" class="login-layout">
		<!-- Background Video -->
		<div class="video-background">
			<video
				ref="videoRef"
				class="bg-video"
				playsinline
				autoplay
				muted
				loop
			>
				<source src="/products-homepage/media/salicru.mp4" type="video/mp4" />
			</video>
			<div class="overlay overlay-grayscale"></div>
			<div class="overlay overlay-gradient"></div>
			<div class="overlay overlay-noise"></div>
		</div>

		<!-- Login Panel -->
		<div class="login-panel">
			<div class="login-content">
				<!-- Header / Logo -->
				<div class="login-header">
					<div class="logo-container">
						<img src="/products-homepage/img/salicru.svg" alt="Salicru" class="salicru-logo" />
					</div>
					<div class="title-container">
						<h1 class="login-title">{{ showForm ? $t('login_user') : $t('login_selection_title') }}</h1>
						<div class="title-underline"></div>
					</div>
				</div>

				<!-- Error Message -->
				<transition name="fade">
					<div v-if="error" class="error-banner">
						<i class="fas fa-exclamation-circle"></i>
						<span>{{ typeof error === 'string' ? $t(error) : $t('login_error') }}</span>
					</div>
				</transition>

				<!-- Provider Selection -->
				<div v-if="!showForm" class="selection-view">
					<div class="selection-buttons">
						<button class="btn-provider btn-o365" @click="$emit('loginO365')">
							<div class="provider-icon o365-logo"></div>
							<span class="provider-text">{{ $t('login_o365') }}</span>
							<i class="fas fa-chevron-right arrow-icon"></i>
						</button>

						<button class="btn-provider btn-local" @click="showForm = true">
							<div class="provider-icon">
								<i class="fas fa-user-circle"></i>
							</div>
							<span class="provider-text">{{ $t('login_user') }}</span>
							<i class="fas fa-chevron-right arrow-icon"></i>
						</button>
					</div>
				</div>

				<!-- Local Login Form -->
				<div v-else class="form-view">
					<form @submit.prevent="handleLocalLogin" class="login-form">
						<div class="input-group">
							<div class="input-icon">
								<i class="fas fa-envelope"></i>
							</div>
							<input
								type="email"
								v-model="email"
								:placeholder="$t('email')"
								required
								:disabled="loading"
								class="login-input"
							/>
						</div>

						<div class="input-group">
							<div class="input-icon">
								<i class="fas fa-lock"></i>
							</div>
							<input
								:type="showPassword ? 'text' : 'password'"
								v-model="password"
								:placeholder="$t('password')"
								required
								:disabled="loading"
								class="login-input"
							/>
							<button
								type="button"
								class="password-toggle"
								@click="showPassword = !showPassword"
								:disabled="loading"
							>
								<i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
							</button>
						</div>

						<div class="form-actions">
							<button type="submit" class="btn-submit" :disabled="loading || !email || !password">
								<span v-if="loading" class="spinner">
									<i class="fas fa-circle-notch fa-spin"></i>
									{{ $t('loading') }}
								</span>
								<template v-else>
									<span class="btn-text">{{ $t('login') }}</span>
									<i class="fas fa-arrow-right"></i>
								</template>
							</button>

							<button
								v-if="!isProductsDomain"
								type="button"
								class="btn-back"
								@click="showForm = false"
								:disabled="loading"
							>
								{{ $t('back') }}
							</button>
						</div>
					</form>
				</div>
			</div>

			<!-- Footer Info -->
			<div class="login-footer">
				<p>© {{ new Date().getFullYear() }} Salicru S.A. {{ $t('copyright_reserved') }}</p>
			</div>
		</div>
	</div>
</template>

<script>
import LocalAuthService from '../resources/local-auth.js';

export default {
	name: 'LoginSelection',
	data() {
		const isProductsDomain = window.location.hostname.includes('products');
		return {
			showForm: isProductsDomain,
			isProductsDomain,
			email: '',
			password: '',
			loading: false,
			error: null,
			showPassword: false,
		};
	},
	methods: {
		handleLocalLogin() {
			this.loading = true;
			this.error = null;
			LocalAuthService.login(this.email, this.password)
				.then(() => {
					this.$emit('loginSuccess');
				})
				.catch((err) => {
					console.error('[Auth] Local login error:', err);
					if (err.response && err.response.status === 403) {
						this.error = 'access_denied_message';
					} else {
						this.error = 'login_error';
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
	},
	mounted() {
		// Handle video autoplay on resize (mimicking React logic)
		const handleVideoAutoplay = () => {
			const video = this.$refs.videoRef;
			if (video) {
				if (window.innerWidth < 1024) {
					video.pause();
				} else {
					video.play().catch(() => {});
				}
			}
		};
		handleVideoAutoplay();
		window.addEventListener('resize', handleVideoAutoplay);
		this.$once('hook:beforeDestroy', () => {
			window.removeEventListener('resize', handleVideoAutoplay);
		});
	},
};
</script>

<style lang="scss" scoped>
// Variables
$salicru-red: #f32121;
$slate-800: #1e293b;
$slate-400: #94a3b8;
$slate-100: #f1f5f9;
$slate-50: #f8fafc;
$white: #ffffff;
$radius: 22px;
$transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

.login-layout {
	position: fixed;
	inset: 0;
	width: 100vw;
	height: 100vh;
	display: flex;
	align-items: stretch;
	overflow: hidden;
	background-color: #000;
	z-index: 9999;
	font-family: 'Univers LT Std Cn', Arial, sans-serif;
}

// Video Background
.video-background {
	position: fixed;
	inset: 0;
	z-index: -1;

	.bg-video {
		width: 100%;
		height: 100%;
		object-fit: cover;
		filter: brightness(0.7);
		transform: scale(1.05);
	}

	.overlay {
		position: absolute;
		inset: 0;
	}

	.overlay-grayscale {
		background-color: rgba(15, 23, 42, 0.2);
		backdrop-filter: grayscale(0.2);
	}

	.overlay-gradient {
		background: linear-gradient(to top right, rgba(15, 23, 42, 0.4), transparent, rgba(127, 29, 29, 0.1));
	}

	.overlay-noise {
		opacity: 0.03;
		pointer-events: none;
		background-image: radial-gradient(#fff 1px, transparent 1px);
		background-size: 32px 32px;
	}
}

// Login Panel
.login-panel {
	position: relative;
	z-index: 10;
	width: 100%;
	background-color: $white;
	display: flex;
	flex-direction: column;
	padding: 2rem;
	overflow-y: auto;
	box-shadow: 20px 0 80px rgba(0, 0, 0, 0.08);

	@media (min-width: 1024px) {
		width: 520px;
		border-radius: 0 40px 40px 0;
	}

	@media (min-width: 1280px) {
		width: 600px;
		padding: 4rem;
	}
}

.login-content {
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: center;
	max-width: 440px;
	margin: 0 auto;
	width: 100%;
}

// Header
.login-header {
	margin-bottom: 2.5rem;

	.logo-container {
		margin-bottom: 1.5rem;
		.salicru-logo {
			height: 52px;
			width: auto;
			display: block;
		}
	}

	.login-title {
		font-family: 'Univers LT Std BoldCn', Arial, sans-serif;
		font-size: 1.8rem;
		font-weight: 700;
		color: $slate-800;
		margin: 0;
		letter-spacing: -0.02em;

		@media (min-width: 768px) {
			font-size: 2.2rem;
		}
	}

	.title-underline {
		width: 48px;
		height: 4px;
		background-color: $salicru-red;
		border-radius: 99px;
		margin-top: 0.5rem;
	}
}

// Error Banner
.error-banner {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 1.25rem;
	background-color: rgba(254, 242, 242, 0.5);
	border: 1px solid #fee2e2;
	border-radius: 16px;
	color: #b91c1c;
	margin-bottom: 2rem;
	font-size: 0.875rem;
	font-weight: 600;
	backdrop-filter: blur(8px);

	i {
		font-size: 1.25rem;
		flex-shrink: 0;
	}
}

// Selection View
.selection-view {
	.selection-buttons {
		display: flex;
		flex-direction: column;
		gap: 1.25rem;
	}
}

.btn-provider {
	display: flex;
	align-items: center;
	width: 100%;
	padding: 1.75rem;
	background-color: $white;
	border: 1px solid $slate-100;
	border-radius: $radius;
	cursor: pointer;
	transition: $transition;
	text-align: left;
	box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);

	&:hover {
		transform: translateY(-3px);
		border-color: rgba($salicru-red, 0.3);
		box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);

		.arrow-icon {
			transform: translateX(4px);
			color: $salicru-red;
		}

		.provider-text {
			color: $salicru-red;
		}
	}

	.provider-icon {
		width: 48px;
		height: 48px;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-right: 1.25rem;
		font-size: 2rem;
		color: $slate-400;

		&.o365-logo {
			background-image: url('https://storage.googleapis.com/sharepoint-assets/img/office365-logo.png');
			background-size: contain;
			background-repeat: no-repeat;
			background-position: center;
		}
	}

	.provider-text {
		flex: 1;
		font-weight: 700;
		color: $slate-800;
		font-size: 1.1rem;
		transition: $transition;
	}

	.arrow-icon {
		color: $slate-100;
		transition: $transition;
	}
}

// Form View
.login-form {
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
}

.input-group {
	position: relative;
	display: flex;
	align-items: center;

	.input-icon {
		position: absolute;
		left: 1.25rem;
		color: $slate-400;
		pointer-events: none;
		transition: $transition;
	}

	.login-input {
		width: 100%;
		padding: 1.25rem 1.25rem 1.25rem 3.5rem;
		background-color: $slate-50;
		border: 1px solid $slate-100;
		border-radius: $radius;
		font-size: 1rem;
		color: $slate-800;
		transition: $transition;
		outline: none;

		&::placeholder {
			color: $slate-400;
		}

		&:focus {
			background-color: $white;
			border-color: $salicru-red;
			box-shadow: 0 0 0 4px rgba(243, 33, 33, 0.05);

			& + .input-icon {
				color: $salicru-red;
			}
		}
	}

	.password-toggle {
		position: absolute;
		right: 1.25rem;
		background: none;
		border: none;
		color: $slate-400;
		cursor: pointer;
		padding: 0.5rem;
		transition: $transition;

		&:hover {
			color: $salicru-red;
		}
	}
}

.form-actions {
	margin-top: 1.5rem;
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.btn-submit {
	width: 100%;
	padding: 1.25rem;
	background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
	color: $white;
	border: none;
	border-radius: $radius;
	font-weight: 700;
	font-size: 0.875rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	cursor: pointer;
	transition: $transition;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.75rem;
	box-shadow: 0 10px 20px rgba(15, 23, 42, 0.15);

	&:hover:not(:disabled) {
		transform: translateY(-2px);
		box-shadow: 0 15px 30px rgba(15, 23, 42, 0.2);
		background: linear-gradient(135deg, #334155 0%, #1e293b 100%);

		i {
			transform: translateX(4px);
		}
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	i {
		transition: $transition;
	}
}

.btn-back {
	padding: 1.25rem;
	background-color: $slate-50;
	border: 1px solid $slate-100;
	color: $slate-800;
	border-radius: $radius;
	font-weight: 700;
	font-size: 0.875rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	cursor: pointer;
	transition: $transition;

	&:hover {
		background-color: $slate-100;
		border-color: $slate-400;
	}
}

.login-footer {
	margin-top: auto;
	padding-top: 2rem;
	text-align: center;
	color: $slate-400;
	font-size: 0.75rem;

	@media (min-width: 1024px) {
		text-align: left;
	}
}

// Animations
.fade-enter-active, .fade-leave-active {
	transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
	opacity: 0;
}

.spinner {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}
</style>
